'use client'
import { useState, memo, useEffect, useRef, useCallback } from 'react'
import type { ChangeEvent, JSX } from 'react'
import type { UseFormRegister as TypeRegister, FieldError, RegisterOptions, FieldValues } from 'react-hook-form'
import styled, { css } from 'styled-components'
import { mq, Col, parsePlaceHolder, presets } from '@/styles'
import { TypeColors } from '@/schema'

interface iTextArea {
  readonly name: string
  readonly label?: string,
  defaultValue?: string
  readonly required?: boolean | RegisterOptions
  readonly onChange?: (event: ChangeEvent<HTMLTextAreaElement>) => void
  readonly onBlur?: (event: ChangeEvent<HTMLTextAreaElement>) => void
  readonly col?: Col
  readonly register?: TypeRegister<FieldValues>
  readonly className?: string
  readonly textColor?: TypeColors
  readonly dropdownTextColor?: TypeColors
  readonly bgColor?: TypeColors
  error?: FieldError | { message: string, [key: string]: unknown }
  readonly disabled?: boolean
  readonly readOnly?: boolean,
  placeholder?: string
  readonly placeholderVisible?: boolean
  readonly lineHeight?: number
  readonly initialHeight?: number
}

interface iDivProps {
  $hasContent: boolean
  $textColor?: TypeColors
  $bgColor: TypeColors
  $col: Col,
  $placeholderVisible: boolean,
  $height: number
}

const hasContentState = () => css`
  ${presets.font18InputHasContentLabel}
  opacity: 1;
  transform: translateY(0) !important;
`

const noRegister = () => ({ onChange: null, onBlur: null, ref: null, name: null })
export const TextArea = memo(({ name, label, required = false, onChange, onBlur, col, register = noRegister, className, textColor = '--color-black', bgColor = '--color-white', error, disabled, readOnly, placeholder, placeholderVisible = false, defaultValue = '', initialHeight = null }:iTextArea):JSX.Element => {
  const requireProps = typeof required === 'boolean' ? { required } : required
  const { ref:registerRef, name:registerName, onChange:registerOnChange, onBlur:registerOnBlur, } = register(name, { ...requireProps })
  const [heightTextArea, setHeightTextArea] = useState(initialHeight || 100)
  const [textareaValue, setTextareaValue] = useState(defaultValue + '')
  const ref = useRef<HTMLTextAreaElement | null>(null)

  const handleChange = e => {
    registerOnChange?.(e)
    onChange?.(e)
    setTextareaValue(e.target.value)
    if (e.target.value.trim().length === 0) {
      setHeightTextArea(25)
    }
  }

  const handleBlur = e => {
    registerOnBlur?.(e)
    onBlur?.(e)
  }

  const onKeyUp = e => {
    if (e.key === 'Enter') {
      if(ref?.current) {
        setTimeout(() => setHeightTextArea(ref.current.scrollHeight), 300)
      }
    }
  }

  const onFocus = e => {
    setTextareaValue(e.target.value)
    if (e.target.value.trim().length === 0) {
      if(ref?.current) {
        setTimeout(() => setHeightTextArea(ref.current.scrollHeight), 300)
      }
    }
  }

  const updateHeight = useCallback(() => {
    if(ref?.current) {
      setTextareaValue(ref.current.value)
      setTimeout(() => setHeightTextArea(ref.current?.scrollHeight), 300)
    }
  }, [])

  useEffect(() => {
    if(!initialHeight) updateHeight()
  }, [initialHeight, updateHeight])

  useEffect(() => {
    window.addEventListener('resize', updateHeight)

    return () => {
      window.removeEventListener('resize', updateHeight)
    }
  }, [updateHeight])

  return (
    <Label $col={col} $textColor={textColor} $bgColor={bgColor} {...className && { className }} $placeholderVisible={placeholderVisible || textareaValue.length > 0} $height={heightTextArea} $hasContent={textareaValue?.length > 0}>
      <textarea name={registerName ?? name} aria-invalid={!!error} onChange={handleChange} onBlur={handleBlur} onFocus={onFocus} disabled={disabled} readOnly={readOnly} placeholder={placeholderVisible ? placeholder : ''} onKeyDown={onKeyUp} defaultValue={defaultValue}
        ref={(e) => {
          ref.current = e
          registerRef?.(e)
        }}
      />
      {label && <span>{label} · ({textareaValue?.length}/250)</span>}
      {error?.message && <span role='alert'>{error.message}</span>}
    </Label>
  )
})



const Label = styled.label<iDivProps>`
  display: block;
  flex-direction: column;
  height: ${({ $height }) => $height ? `${$height}px` : 'auto'};
  justify-content: flex-end;
  margin-bottom: 30px;
  min-height: 20px;
  padding: 0 4.25px;
  position: relative;
  width: 100%;

  ${mq.greaterThan('tablet')} {
    margin-bottom: 30px;
    min-height: 15px;
    padding: 0 12px;
    width: ${({ $col }) => typeof $col === 'number' ? `${100 * $col / 12}%` : $col };
  }

  ${({ $placeholderVisible }) => parsePlaceHolder(`
    color: ${$placeholderVisible ? 'var(--color-black)' : 'rgba(0, 0, 0 ,0)'};
    opacity: ${$placeholderVisible ? .4 : 0};
  `)}

  > span {
    &:first-of-type {
      ${({ $placeholderVisible }) => $placeholderVisible ? hasContentState() : presets.font18InputLabel}
      color: var(--color-black);
      opacity: .4;
      position: absolute;
      top: 0;
      transition: 300ms font-size ease, 300ms transform ease, 300ms opacity ease;
      transform: translateY(30px);
      white-space: nowrap;

      ${({ $hasContent }) => $hasContent ? css`${hasContentState()}` : ''}
    }

    &[role='alert'] {
      ${presets.font15}
      bottom: -55px;
      color: var(--color-input_error);
      font-weight: 400;
      left: 0;
      padding: 0 4.25px;
      pointer-events: none;
      position: absolute;
      top: 0;

      ${mq.greaterThan('tablet')} {
        padding: 0 12.5px;
      }
    }
  }

  textarea {
    ${presets.font15}
    -moz-border-radius: 0;
    -webkit-border-radius: 0;
    background-color: var(--color-white);
    border-bottom: none;
    border-color: unset;
    border-left: none;
    border-radius: 0;
    border-right: none;
    border-top: none;
    box-sizing: border-box; /* Include padding and border in the height */
    display: block;
    font-family: var(--font-inter), sans-serif;
    height: ${({ $height }) => $height ? `${$height}px` : 'auto'};
    line-height: 1.5; /* Match this in the clone */
    margin: 25px 0 0;
    outline: none;
    position: absolute;
    resize: none;
    top: 0;
    width:98%;

    &:focus {
      border-color: var(--color-black);

      + span {
        ${hasContentState()}
        color: var(--color-black);
        opacity: 1;
      }
    }

    &[readonly],
    &[disabled] {
      cursor: auto;
      pointer-events: none;
    }

    &[readonly] {
      opacity: .3;

      + span {
        opacity: .3;
      }
    }

    &[disabled] {
      opacity: .2;

      + span {
        opacity: .2;
      }
    }

    &[aria-invalid='true'] {
      border-color: var(--color-input_error);
    }
  }
`
